/* eslint-disable no-useless-escape */
/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */
import classes from '~/config/classes'
import { convertToHandle } from '~/helpers/convert'

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function debounceFunc(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Get ordinal based on number.
 * @param {Number} number - Number.
 * @returns {String}
 */
export function getNumberOrdinal(number, superscript = false) {
  let ordinal = 'other'

  if (number % 10 === 1 && number % 100 !== 11) {
    ordinal = 'first'
  } else if (number % 10 === 2 && number % 100 !== 12) {
    ordinal = 'second'
  } else if (number % 10 === 3 && number % 100 !== 13) {
    ordinal = 'third'
  }

  return superscript
    ? cnvs.strings.number_ordinals_superscript[ordinal]
    : cnvs.strings.number_ordinals[ordinal]
}

/**
 * Handles pluralisation of strings based on Unicode plural rules.
 * - https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
 * - Follows English plural rules.
 * - Zero + (One|Single) + (Two + Few) + (Other|Many|Plural).
 * @param {Object} strings - Strings object containing locale strings.
 * @param {Number} number - Number to use for plural rules.
 * @returns {String}
 */
export function handlePluralisation(strings, number) {
  if (strings.zero && number === 0) {
    return strings.zero
  }

  const one = strings.one || strings.single

  if (one && number === 1) {
    return one
  }

  if (strings.two && number === 2) {
    return strings.two
  }

  const many = strings.other || strings.many || strings.plural

  if (strings.few) {
    if (many && strings.two && number === 3) {
      return strings.few
    }

    if (!many && strings.two && number > 2) {
      return strings.few
    }

    if (!many && number > 1) {
      return strings.few
    }
  }

  return many
}

/**
 * Test if a URL is a Shopify CDN URL.
 * - Returns true if URL contains:
 * -- cdn.shopify.com/
 * -- [custom-domain].[tld]/cdn/
 * -- [store].myshopify.com/cdn/
 * @param {String} url - URL to test.
 * @returns {Boolean}
 */
export function isCdnUrl(url) {
  const cdnUrls = [
    'cdn.shopify.com/',
    `${cnvs.store.shopDomain.replaceAll('/', '')}/cdn/`,
    `${cnvs.store.permanentDomain.replaceAll('/', '')}/cdn/`,
  ]

  return url && cdnUrls.some((cdnUrl) => url.includes(cdnUrl))
}

/**
 * Determine whether a storage error is a QuotaExceededError.
 * - I.e. there isn't enough room to store the data.
 * @param {*} error - Error to check.
 * @returns {Boolean}
 */
function isQuotaExceededError(error) {
  return (
    error instanceof DOMException &&
    (
      [22, 1014].includes(error.code) ||
      ['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED'].includes(error.name)
    )
  )
}

/**
 * Determine if sessionStorage is supported.
 * - Adapted from https://mmazzarolo.com/blog/2022-06-25-local-storage-status/
 * @param {String} [storageType] - Type of storage to test.
 * @returns {Boolean}
 */
export function isStorageSupported(storageType = 'sessionStorage') {
  let storage = false

  try {
    storage = window[storageType]

    if (!storage) {
      return false
    }

    const test = `__storage_test__`

    storage.setItem(test, test)
    storage.removeItem(test)
    return true

  } catch (error) {

    /**
     * We acknowledge a QuotaExceededError only if there's something stored.
     */
    const isValidQuotaExceededError = isQuotaExceededError(error) && storage.length
    return isValidQuotaExceededError
  }
}

/**
 * Test if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @param {Object} [config] - Config options.
 * @param {HTMLElement} [config.container] - Container to check in.
 * @param {String} [config.direction] - Either `vertical` or `horizontal`.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(
  element,
  {
    container,
    direction = 'vertical',
    offsetEnd = 0,
    offsetStart = 0,
  } = {},
) {
  let containerDimension = container
    ? container.offsetHeight
    : window.innerHeight

  const elementRectangle = element.getBoundingClientRect()
  let elementStart = elementRectangle.top
  let elementEnd = elementRectangle.bottom

  if (direction === 'horizontal') {
    containerDimension = container
      ? container.offsetWidth
      : window.innerWidth

    elementStart = elementRectangle.left
    elementEnd = elementRectangle.right
  }

  elementStart = elementStart - offsetStart > 0 ? elementStart - offsetStart : 0
  elementEnd += offsetEnd

  const startInView = (elementStart >= 0) && (elementStart < containerDimension)
  const endInView = (elementEnd > 0) && (elementEnd <= containerDimension)

  return startInView || endInView
}

/**
 * Move asynchronous element after it's rendered.
 * @param {String|Array} selectors - Element selectors.
 * @param {HTMLElement} target - Target destination.
 * @param {Function|null} callback - A callback function to execute on the
 * container found, before it is moved.
 */
export function moveAsyncElement(selectors, target, callback = null) {
  const moveInterval = setInterval(() => {
    let elements = []

    if (typeof selectors === 'string') {
      const element = document.querySelector(selectors)

      if (!element) {
        return
      }

      elements.push(element)

    } else {
      elements = selectors
        .map((selector) => {
          const element = document.querySelector(selector)

          if (!element) {
            return false
          }

          return element
        })
        .filter(Boolean)
    }

    if (!elements.length) {
      return
    }

    clearInterval(moveInterval)

    elements.forEach((element) => {
      target.appendChild(element)
      element.classList.remove(classes.hidden)

      if (typeof callback !== 'function') {
        return
      }

      callback(element)
    })
  }, 500)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} [config] - Additional data.
 * @param {Function} [config.callback] - Function run after completion.
 * @param {String} [config.headerState] - State of header when function is run.
 * @param {HTMLElement} [config.page] - Element to scroll.
 * @param {String} [config.behavior] - Set scroll behavior.
 */
export function scrollToElement(element, { callback, headerState, page = window, behavior = 'smooth' } = {}) {

  let viewportFromPageTop = window.scrollY

  if (page !== window) {
    viewportFromPageTop = 0
  }

  /**
   * Get element scroll position.
   */
  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = Math.floor(elementRectangle.top)
  let position = viewportFromPageTop + elementFromViewportTop

  /**
   * Retrieve variables.
   */
  const headerHeight = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .replace('px', ''),
  )

  const fontSize = Number(
    getComputedStyle(document.documentElement).fontSize
      .replace('px', ''),
  )

  let spacingM = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--spacing-m')
      .replace('rem', ''),
  )

  spacingM *= fontSize

  /**
   * Offset height of header and update position.
   * - Assumes that when header is collapsed it has a height of 0.
   * - If it doesn't then update each condition with a new variable.
   * - Don't use --header-visible-height as that isn't updated in time.
   */
  const threshold = headerState === 'collapsed'
    ? spacingM
    : headerHeight + spacingM

  if (elementFromViewportTop === threshold) {
    return
  }

  const dynamicHeaderHeight = elementFromViewportTop > threshold
    ? 0
    : headerHeight

  position -= dynamicHeaderHeight
  position -= spacingM

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : behavior

  page.scroll({
    scrollBehavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set initial active merge object in main product and dickey jacket content.
 * @param {Object} product - Product data.
 * @param {Object} legacyGroup - Legacy colour swatch group.
 * @returns {Boolean|Object}
 */
export function setInitialActiveMerge(product, legacyGroup) {

  /**
   * Determine product variant's colour.
   */
  const colourOptionName = cnvs.settings.product.productSwatchOptionNames

  const colourOptionIndex = product.options?.findIndex((option) => {
    return colourOptionName?.includes(option.name?.toLowerCase())
  })

  if (colourOptionIndex < 0) {
    return false
  }

  if (legacyGroup) {
    return legacyGroup
  }

  const firstVariant = product?.variants?.[0]
  const colour = firstVariant?.selectedOptions[colourOptionIndex]?.value
  const colourHandle = convertToHandle(`${colour}-${product.handle}`)

  /**
   * Search each extension's grouping and then product grouping for matching
   * object to set as active.
   */
  let activeMerge = false

  product.extensions?.forEach((object) => {
    activeMerge = object.extensionGrouping?.find((findObject) => {
      return findObject?.swatch?.handle === colourHandle
    })
  })

  if (activeMerge) {
    return activeMerge
  }

  return product.grouping?.find((findObject) => {
    return findObject?.swatch?.handle === colourHandle
  })
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - Values to grow or shrink each side of the
 * root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Object} - Observer instance
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)

  return observer
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function throttleFunc(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}

/**
 * Highlight 'highlighted' string in 'text'.
 * @param {String} text
 * @param {String} highlighted
 */
export function getHighlightedText(text, highlighted) {
  if (!highlighted) {
    return text
  }

  const groups = highlighted.split(' ').map(word => `(${word})`).join('|')

  return `
    <span>
      ${text.replace(new RegExp(groups, 'gi'), matched => `<strong>${matched}</strong>`)}
    <span>
  `
}
